import { useEffect, useRef } from "react";
import { useMutation } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm, type SubmitHandler } from "react-hook-form";
import { z } from "zod";

import { FieldGroupSurface } from "@/components/field-group-surface";
import { SaveStateInline } from "@/components/save-state-inline";
import { SectionBlock } from "@/components/section-block";
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  UPDATE_DRAFT_PREVIEW_INTEGRATE,
  type DraftPreview,
  type UpdateDraftPreviewIntegrateData,
  type UpdateDraftPreviewIntegrateVariables,
} from "@/graphql";
import { useAutoSave } from "@/hooks/use-auto-save";
import { LANGUAGE_OPTIONS } from "@/lib/languages";

const integrateSchema = z.object({
  pixel: z.string(),
  googleAnalytics4: z
    .string()
    .refine(
      (v) => v === "" || /^G-[a-zA-Z0-9-]+$/i.test(v),
      "Google Analytics 4 measurement ID must be in G-XXXXX format."
    ),
  language: z.string(),
});

export type IntegrateFormValues = z.infer<typeof integrateSchema>;

interface IntegrateFormProps {
  instagramUsername: string;
  draftPreview: DraftPreview;
}

export function IntegrateForm({
  instagramUsername,
  draftPreview,
}: IntegrateFormProps) {
  const form = useForm<IntegrateFormValues>({
    resolver: zodResolver(integrateSchema),
    defaultValues: toFormValues(draftPreview),
    mode: "onChange",
  });
  const hydratedUpdatedAtRef = useRef(draftPreview.updatedAt);

  useEffect(() => {
    if (hydratedUpdatedAtRef.current === draftPreview.updatedAt) return;
    if (form.formState.isDirty) return;

    hydratedUpdatedAtRef.current = draftPreview.updatedAt;
    form.reset(toFormValues(draftPreview));
  }, [draftPreview, form]);

  const [updateIntegrate] = useMutation<
    UpdateDraftPreviewIntegrateData,
    UpdateDraftPreviewIntegrateVariables
  >(UPDATE_DRAFT_PREVIEW_INTEGRATE);

  const onSubmit: SubmitHandler<IntegrateFormValues> = async (values) => {
    await updateIntegrate({
      variables: {
        instagramUsername,
        pixel: values.pixel,
        googleAnalytics4: values.googleAnalytics4.trim(),
        language: values.language || null,
      },
    });
  };

  const autoSaveState = useAutoSave({
    form,
    onSave: async (values) => {
      await onSubmit(values);
      return true;
    },
    delayMs: 500,
  });

  const { isSubmitting, isValid } = form.formState;

  return (
    <FormProvider {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-10">
        <SectionBlock
          title="Tracking"
          description="Connect external analytics tools so you can measure visits and conversions."
        >
          <FieldGroupSurface className="space-y-4">
            <FormField
              control={form.control}
              name="pixel"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Facebook Pixel ID</FormLabel>
                  <FormControl>
                    <Input placeholder="XXXXXXXXX" {...field} />
                  </FormControl>
                  <FormDescription>
                    Get this ID from your Facebook business manager.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="googleAnalytics4"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Google Analytics 4 (GA4) ID</FormLabel>
                  <FormControl>
                    <Input placeholder="G-XXXXXXX" {...field} />
                  </FormControl>
                  <FormDescription>
                    Get this ID from Google Analytics.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldGroupSurface>
        </SectionBlock>

        <SectionBlock
          title="Accessibility"
          description="Set the page language so assistive technologies and search engines interpret the content correctly."
        >
          <FieldGroupSurface>
            <FormField
              control={form.control}
              name="language"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Language</FormLabel>
                  <FormControl>
                    <Select
                      value={field.value || undefined}
                      onValueChange={field.onChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Pick a language" />
                      </SelectTrigger>
                      <SelectContent>
                        {LANGUAGE_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FormControl>
                  <FormDescription>
                    Pick the language your content is in for screen readers and
                    SEO.
                  </FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </FieldGroupSurface>
        </SectionBlock>

        <SaveStateInline
          isSubmitting={isSubmitting}
          isValid={isValid}
          isDirty={autoSaveState.hasUnsavedChanges}
          isAutoSaving={autoSaveState.isSaving}
          isAutoSavePending={autoSaveState.isPending}
          autoSaveErrorMessage={autoSaveState.errorMessage}
        />
      </form>
    </FormProvider>
  );
}

function toFormValues(preview: DraftPreview): IntegrateFormValues {
  return {
    pixel: preview.pixel ?? "",
    googleAnalytics4: preview.googleAnalytics4 ?? "",
    language: preview.language ?? "",
  };
}
